/**
 * useSectionEngagement — lightweight per-section homepage analytics.
 *
 * Tracks three things via the existing funnel pipeline:
 *   1. `entered`     — fires once when ≥50% of the section is in viewport.
 *   2. `dwelled`     — fires once when accumulated visible time ≥ 1.5s
 *                      (carries `extras.ms` so we can compute avg dwell).
 *   3. `cta_clicked` — fires when the consumer calls the returned helper
 *                      (typically inside an existing CTA onClick handler).
 *
 * All events go through `trackFunnel("homepage", stage, { section })`
 * which mirrors to PostHog + the backend `/api/analytics/funnel` endpoint.
 *
 * Usage:
 *   const [ref, markCtaClicked] = useSectionEngagement("hero");
 *   <section ref={ref}>...
 *     <Button onClick={() => { markCtaClicked(); doYourThing(); }} />
 *   </section>
 */
import { useEffect, useRef, useCallback } from "react";
import { trackFunnel } from "../utils/analytics";

const DWELL_THRESHOLD_MS = 1500;
const VIEWPORT_THRESHOLD = 0.5;

export function useSectionEngagement(sectionId) {
  const ref = useRef(null);
  const enteredRef = useRef(false);
  const dwellFiredRef = useRef(false);
  const enterTimeRef = useRef(null);
  const accumulatedMsRef = useRef(0);
  const isVisibleRef = useRef(false);

  // The IntersectionObserver only tracks visibility transitions. Accumulating
  // dwell time happens in this single helper so logic stays in one place.
  const flushAccumulator = useCallback(() => {
    if (isVisibleRef.current && enterTimeRef.current != null) {
      const now = Date.now();
      accumulatedMsRef.current += now - enterTimeRef.current;
      enterTimeRef.current = now;
    }
    if (!dwellFiredRef.current && accumulatedMsRef.current >= DWELL_THRESHOLD_MS) {
      dwellFiredRef.current = true;
      trackFunnel("homepage", "dwelled", {
        section: sectionId,
        ms: Math.round(accumulatedMsRef.current),
      });
    }
  }, [sectionId]);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            isVisibleRef.current = true;
            enterTimeRef.current = Date.now();
            if (!enteredRef.current) {
              enteredRef.current = true;
              trackFunnel("homepage", "entered", { section: sectionId });
            }
          } else {
            // Section left viewport — accumulate elapsed time, possibly fire dwell.
            flushAccumulator();
            isVisibleRef.current = false;
            enterTimeRef.current = null;
          }
        }
      },
      { threshold: VIEWPORT_THRESHOLD },
    );
    observer.observe(node);

    // Periodic flush — handles the "user opens, never scrolls" case where
    // the section stays visible the whole time. 800ms tick keeps overhead minimal.
    const tickId = setInterval(flushAccumulator, 800);

    // Page-hide / tab-switch — flush before the user navigates away.
    const onHide = () => flushAccumulator();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);

    return () => {
      observer.disconnect();
      clearInterval(tickId);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      flushAccumulator();
    };
  }, [sectionId, flushAccumulator]);

  // Consumers wire this into existing CTA onClick handlers.
  const markCtaClicked = useCallback((extras = {}) => {
    flushAccumulator();
    trackFunnel("homepage", "cta_clicked", {
      section: sectionId,
      ms_before_click: Math.round(accumulatedMsRef.current),
      ...extras,
    });
  }, [sectionId, flushAccumulator]);

  return [ref, markCtaClicked];
}

export default useSectionEngagement;
